import { canvasPaint } from "./native-gradient";
import { measuredWidth, onTextCacheReset } from "./text-cache";
import { type RenderContext, renderCanvas } from "./canvas-surface";
import type { SceneNode } from "../scene/model";
import { curvedText, textCurveBox, textPose } from "../native/text-deformation";
import {
  characterStyle,
  textData,
  textFont,
  type CharacterStyle,
} from "../native/text";
export interface TextGlyph {
  index: number;
  value: string;
  x: number;
  y: number;
  width: number;
  height: number;
  style: CharacterStyle;
}
export interface TextLayout {
  glyphs: TextGlyph[];
  width: number;
  height: number;
  carets: { x: number; y: number; height: number }[];
}
let measure: RenderContext;
const layouts = new Map<SceneNode["text"], TextLayout>();
let cachedGlyphs = 0;
onTextCacheReset(() => {
  layouts.clear();
  cachedGlyphs = 0;
});
export const textLayoutStats = () => ({
  layouts: layouts.size,
  glyphs: cachedGlyphs,
  budgetGlyphs: 65536,
});
export function textLayout(node: SceneNode): TextLayout {
  const cached = layouts.get(node.text);
  if (cached) {
    layouts.delete(node.text);
    layouts.set(node.text, cached);
    return cached;
  }
  measure ??= renderCanvas().getContext("2d")!;
  const t = textData(node),
    defaultStyle = characterStyle(t, -1),
    styleAt = (index: number) =>
      t.styles[index] ? characterStyle(t, index) : defaultStyle,
    glyphs: TextGlyph[] = [],
    carets: TextLayout["carets"] = [];
  let line: TextGlyph[] = [],
    x = 0,
    y = 0,
    lineHeight = t.size * t.lineSpacing * (t.metrics?.lineHeight ?? 1),
    max = 0;
  const finish = () => {
    const shift =
      t.align === "center"
        ? (t.layoutWidth - x) / 2
        : t.align === "right"
          ? t.layoutWidth - x
          : 0;
    for (const g of line) {
      g.x += shift;
      g.y = y;
      g.height = lineHeight;
      carets[g.index] = { x: g.x, y, height: lineHeight };
      glyphs.push(g);
    }
    max = Math.max(max, x);
    y += lineHeight;
    line = [];
    x = 0;
    lineHeight = t.size * t.lineSpacing * (t.metrics?.lineHeight ?? 1);
  };
  for (let i = 0; i < t.value.length;) {
    const char = String.fromCodePoint(t.value.codePointAt(i)!);
    const s = styleAt(i);
    measure.font = textFont(s);
    const w = Math.max(0, measuredWidth(measure, char) + t.letterSpacing);
    const previous = line.at(-1);
    if (
      t.metrics?.kerning &&
      previous &&
      textFont(previous.style) === textFont(s)
    ) {
      const kern =
        measuredWidth(measure, previous.value + char) -
        measuredWidth(measure, previous.value) -
        measuredWidth(measure, char);
      x += kern;
    }
    if (char === "\n") {
      carets[i] = { x, y, height: lineHeight };
      finish();
      i++;
      carets[i] = { x: 0, y, height: lineHeight };
      continue;
    }
    // Move a whole word where it fits, otherwise wrap long words by character.
    if (
      t.frame &&
      x > 0 &&
      char !== " " &&
      (i === 0 || /\s/.test(t.value[i - 1]!))
    ) {
      let word = 0;
      for (let j = i; j < t.value.length && !/\s/.test(t.value[j]!); j++) {
        const ws = styleAt(j);
        measure.font = textFont(ws);
        word += measuredWidth(measure, t.value[j]!) + t.letterSpacing;
      }
      if (word <= t.layoutWidth && x + word > t.layoutWidth) finish();
    }
    if (t.frame && x > 0 && x + w > t.layoutWidth) finish();
    lineHeight = Math.max(
      lineHeight,
      s.size * t.lineSpacing * (t.metrics?.lineHeight ?? 1),
    );
    line.push({
      index: i,
      value: char,
      x,
      y,
      width: w,
      height: lineHeight,
      style: s,
    });
    x += w;
    i += char.length;
  }
  const lastIndex = t.value.length,
    lastX = x,
    lastY = y,
    lastHeight = lineHeight;
  finish();
  carets[lastIndex] = {
    x:
      lastX +
      (t.align === "center"
        ? (t.layoutWidth - lastX) / 2
        : t.align === "right"
          ? t.layoutWidth - lastX
          : 0),
    y: lastY,
    height: lastHeight,
  };
  for (let i = 0; i <= lastIndex; i++)
    carets[i] ??= carets[i - 1] ?? {
      x: 0,
      y: 0,
      height: t.size * t.lineSpacing * (t.metrics?.lineHeight ?? 1),
    };
  const result = {
    glyphs,
    width: Math.max(1, max),
    height: t.metrics
      ? Math.max(1, y - lastHeight + lastHeight / t.lineSpacing)
      : Math.max(t.size * t.lineSpacing, y),
    carets,
  };
  if (t.value.length <= 65536) {
    while (cachedGlyphs + t.value.length > 65536 || layouts.size >= 128) {
      const first = layouts.keys().next().value!;
      cachedGlyphs -= String(first.value ?? "").length;
      layouts.delete(first);
    }
    layouts.set(node.text, result);
    cachedGlyphs += t.value.length;
  }
  return result;
}
export function measureTextNode(node: SceneNode, previous = node): SceneNode {
  const t = textData(node);
  if (t.frame) return node;
  const layout = textLayout(node),
    oldBox = textCurveBox(textData(previous)),
    sx = node.geometry.width! / oldBox.width,
    sy = node.geometry.height! / oldBox.height;
  const width = Math.max(1, layout.width),
    height = layout.height;
  // Retain the chosen left/centre/right anchor, top and existing object scale
  // when typing or formatting changes a plain label's size.
  const angle = ((node.transform.rotation ?? 0) * Math.PI) / 180,
    c = Math.cos(angle),
    s = Math.sin(angle),
    k = Math.tan(((node.transform.skewX ?? 0) * Math.PI) / 180);
  const nextText = { ...t, layoutWidth: width, layoutHeight: height },
    box = textCurveBox(nextText),
    dx =
      (box.width - oldBox.width) *
      sx *
      (t.align === "left" ? 0.5 : t.align === "right" ? -0.5 : 0),
    dy = ((box.height - oldBox.height) * sy) / 2;
  return {
    ...node,
    geometry: {
      ...node.geometry,
      width: box.width * sx,
      height: box.height * sy,
      ...(node.geometry.warpSourceSize
        ? {
            warpSourceSize: {
              width:
                ((node.geometry.warpSourceSize as { width: number }).width *
                  box.width) /
                oldBox.width,
              height:
                ((node.geometry.warpSourceSize as { height: number }).height *
                  box.height) /
                oldBox.height,
            },
          }
        : {}),
    },
    transform: {
      ...node.transform,
      x: node.transform.x! + c * dx + (c * k - s) * dy,
      y: node.transform.y! + s * dx + (s * k + c) * dy,
    },
    text: nextText,
  };
}
export function paintText(ctx: RenderContext, node: SceneNode) {
  const t = textData(node),
    layout = textLayout(node),
    w = node.geometry.width!,
    h = node.geometry.height!,
    box = textCurveBox(t),
    curved = curvedText(t);
  ctx.scale(w / box.width, h / box.height);
  ctx.translate(-box.width / 2 - box.x, -box.height / 2 - box.y);
  if (t.frame && !curved) {
    ctx.beginPath();
    ctx.rect(0, 0, t.layoutWidth, t.layoutHeight);
    ctx.clip();
  }
  const paints = new Map<unknown, string | CanvasGradient>();
  const paint = (color: string, gradient: unknown) => {
    if (!gradient) return color;
    if (!paints.has(gradient))
      paints.set(
        gradient,
        canvasPaint(ctx, color, gradient, t.layoutWidth, t.layoutHeight, 0, 0),
      );
    return paints.get(gradient)!;
  };
  ctx.textBaseline = "alphabetic";
  // Stroke the entire text before filling any glyph. Interleaving the two
  // lets a following glyph's outline erase the preceding glyph's face.
  for (const stroke of t.strokeWidth > 0 ? [true, false] : [false]) {
    for (const g of layout.glyphs) {
      // Avoid issuing draw calls for lines far outside an editing frame. Keep a
      // conservative two-em overhang margin, including stroke and custom metrics.
      if (t.frame && g.y - g.style.size * 2 - t.strokeWidth > t.layoutHeight)
        continue;
      ctx.font = textFont(g.style);
      if (stroke) ctx.strokeStyle = paint(t.stroke, t.strokeGradient);
      else ctx.fillStyle = paint(g.style.color, g.style.colorGradient);
      ctx.lineWidth = t.strokeWidth;
      ctx.lineJoin = "round";
      const baseline =
        g.y +
        (t.metrics
          ? g.height / t.lineSpacing / t.metrics.lineHeight
          : g.style.size) *
          (t.metrics?.baseline ?? 0.9);
      const alpha = ctx.globalAlpha;
      let x = g.x,
        y = baseline;
      if (curved) {
        const center = g.x + g.width / 2,
          pose = textPose(t, center, baseline);
        ctx.save();
        ctx.translate(pose.x, pose.y);
        ctx.rotate(pose.angle);
        if (t.frame) {
          ctx.beginPath();
          ctx.rect(-center, -baseline, t.layoutWidth, t.layoutHeight);
          ctx.clip();
        }
        x = -g.width / 2;
        y = 0;
      }
      ctx.globalAlpha =
        alpha * (stroke ? (t.strokeAlpha ?? 1) : (g.style.colorAlpha ?? 1));
      if (stroke) ctx.strokeText(g.value, x, y);
      else ctx.fillText(g.value, x, y);
      if (!stroke && g.style.underline)
        ctx.fillRect(
          x,
          y + g.style.size * 0.1,
          g.width,
          Math.max(1, g.style.size / 18),
        );
      ctx.globalAlpha = alpha;
      if (curved) ctx.restore();
    }
  }
}
